// 顾客分析 — website-service /aggs/passengerflowaggs/*
// 反编译 com/ulucu/model/thridpart/http/manager/customer/CustomerComm.java
import { callInternal } from './internal.js';

const SERVICE = 'website';

export interface AggBucket {
	type: string;        // age type 1-9 / gender 0男1女 / identity 0非会员1会员
	value: string;       // 计数
	stay_time?: string;
}

export interface CustomerOverview {
	age_aggs: AggBucket[];
	gender_aggs: AggBucket[];
	identity_aggs: AggBucket[];
	arrivenum_eq_aggs: AggBucket[];
	overview_max_aggs: {
		max_age_ranges: string;       // "25-31"
		max_all_identity: string;     // "0" / "1"
		max_all_sex: string;          // "0" / "1"
		max_new_old_ranges: string;
	};
}

export interface CrossAgg {
	type: string;
	value: string;
	all_sex?: AggBucket[];
	all_identity?: AggBucket[];
}

export interface CustomerOverviewMore {
	age_aggs1gender_aggs: CrossAgg[];     // age × gender
	age_aggs1identity_aggs: CrossAgg[];   // age × identity
	identity_aggs1gender_aggs: CrossAgg[]; // identity × gender
}

export type AgeTypeMap = Record<string, string>;   // {'1':'18-24'} etc.

interface CommonReq {
	storeIds: string;
	startDate: string;      // YYYY-MM-DD
	endDate: string;
	model_id?: number;      // 1=精准 2=近景
}

function assertOk<T>(res: { code: number; msg: string; data?: T }): T | null {
	if (Number(res.code) === 0) return res.data ?? null;
	throw new Error(`${res.msg || 'Request failed'} (code=${res.code})`);
}

export async function fetchAgeTypeMap(): Promise<AgeTypeMap> {
	const res = await callInternal<AgeTypeMap>({
		short_name: SERVICE,
		action: '/aggs/passengerflowaggs/get_age_type_maps',
		method: 'POST',
		body: { },
	});
	return res.code === 0 ? (res.data ?? {}) : {};
}

export async function fetchCustomerOverview(req: CommonReq): Promise<CustomerOverview | null> {
	const res = await callInternal<CustomerOverview>({
		short_name: SERVICE,
		action: '/aggs/passengerflowaggs/customer_overview',
		method: 'POST',
		body: {
			start_date: req.startDate,
			end_date: req.endDate,
			store_ids: req.storeIds,
			model_id: req.model_id ?? 1,
			group_id: 0,
		},
	});
	return assertOk(res);
}

export async function fetchCustomerOverviewMore(req: CommonReq): Promise<CustomerOverviewMore | null> {
	const res = await callInternal<CustomerOverviewMore>({
		short_name: SERVICE,
		action: '/aggs/passengerflowaggs/customer_overview_more_aggs',
		method: 'POST',
		body: {
			start_date: req.startDate,
			end_date: req.endDate,
			store_ids: req.storeIds,
			model_id: req.model_id ?? 1,
			group_id: 0,
		},
	});
	return assertOk(res);
}

/** 精准客流总览（数字卡片）— /recognition/face/passengerflow/newoverview */
export interface PreciseFlowOverview {
	pass_count: string;        // 过店客流
	all: string;               // 进店客流（精准）
	first: string;             // 首次进店
	firstplus: string;         // 首次后进店
	member: string;            // 到店会员
	try: string;               // 试穿客流
	trade_count: string;       // 成交单数
	stay_avg: string;          // 人均停留时长（分）
	stay_time: string;
	have_stay_time_all: string;
	staff_performance: string; // 人效
	store_performance: string; // 坪效
	rate: { all: string; pass_count: string };  // 进店转化率分子/分母
}

export async function fetchPreciseOverview(req: CommonReq): Promise<PreciseFlowOverview | null> {
	const res = await callInternal<PreciseFlowOverview>({
		short_name: SERVICE,
		action: '/recognition/face/passengerflow/newoverview',
		query: {
			start_date: req.startDate,
			end_date: req.endDate,
			store_ids: req.storeIds,
		},
	});
	return assertOk(res);
}

/** 日粒度/小时粒度 客流时序 — /aggs/passengerflowaggs/passenger_flow */
export interface FlowSeriesPoint {
	current_date?: string;     // YYYY-MM-DD（aggs_time_type=2）
	current_value: string;
	prev_date?: string;
	prev_value?: string;
	hour?: string;             // 小时（aggs_time_type=1）
}

export interface FlowSeriesResponse {
	passengers: Record<string, FlowSeriesPoint>;
	static: {
		average: string;
		max: { date?: string; hour?: string; value: string };
		min: { date?: string; hour?: string; value: string };
	};
}

export async function fetchFlowSeries(req: CommonReq & { aggs_time_type: 1 | 2 }): Promise<FlowSeriesResponse | null> {
	const res = await callInternal<FlowSeriesResponse>({
		short_name: SERVICE,
		action: '/aggs/passengerflowaggs/passenger_flow',
		method: 'POST',
		body: {
			aggs_time_type: req.aggs_time_type,
			start_date: req.startDate,
			end_date: req.endDate,
			store_ids: req.storeIds,
			model_id: req.model_id ?? 1,
			group_id: 0,
		},
	});
	return assertOk(res);
}

export async function fetchPassengerFlow(req: CommonReq & { aggs_time_type?: number }): Promise<unknown> {
	const res = await callInternal({
		short_name: SERVICE,
		action: '/aggs/passengerflowaggs/passenger_flow',
		method: 'POST',
		body: {
			aggs_time_type: req.aggs_time_type ?? 1,
			start_date: req.startDate,
			end_date: req.endDate,
			store_ids: req.storeIds,
			group_id: 0,
			model_id: req.model_id ?? 1,
		},
	});
	return res.code === 0 ? res.data : null;
}
